import { ENDPOINTS } from "utils/endpoints";
import {
  DELETE_API_CALL,
  DELETE_S3_FILES,
  GET_API_CALL,
  GET_READ_SIGNED_URLS,
  PATCH_API_CALL,
  POST_API_CALL,
} from "utils/services";
import { APIResponseModal, ReducersModal } from "utils/modals";
import { UPDATE_TESTIMONIAL_REDUCER } from "store/actionTypes";
import SHOW_TOAST from "utils/showToast";
import { mapTestimonialListResponse } from "utils/apiMappers";
import { toServiceCategory } from "utils/serviceCategory";
import {
  hasAwsSignedQuery,
  isLocalPreviewUrl,
  isStoredMediaUrl,
} from "utils/commonFunctions";

let listRequestKey = "";

const storedUrlForSigning = (url?: string) => {
  const value = `${url || ""}`.trim();
  if (!value || isLocalPreviewUrl(value) || hasAwsSignedQuery(value)) return "";
  if (!isStoredMediaUrl(value)) return "";
  return value;
};

const attachDisplayUrls = async (items: any[] = []) => {
  const targets: { index: number; field: "media" | "thumbnail" }[] = [];
  const urls: string[] = [];

  items.forEach((item, index) => {
    const mediaUrl = storedUrlForSigning(item?.media);
    if (mediaUrl) {
      targets.push({ index, field: "media" });
      urls.push(mediaUrl);
    }
    const thumbnailUrl = storedUrlForSigning(item?.thumbnail);
    if (thumbnailUrl) {
      targets.push({ index, field: "thumbnail" });
      urls.push(thumbnailUrl);
    }
  });

  const withDefaults = items.map((item) => ({
    ...item,
    mediaPreview: item?.mediaPreview || item?.media || "",
    thumbnailPreview: item?.thumbnailPreview || item?.thumbnail || "",
  }));

  if (!urls.length) return withDefaults;

  try {
    const signedUrls = await GET_READ_SIGNED_URLS(urls);
    return withDefaults.map((item, index) => {
      const next = { ...item };
      targets.forEach((target, signedIndex) => {
        if (target.index !== index) return;
        const signed = signedUrls[signedIndex];
        if (!signed) return;
        if (target.field === "media") next.mediaPreview = signed;
        if (target.field === "thumbnail") next.thumbnailPreview = signed;
      });
      return next;
    });
  } catch {
    return withDefaults;
  }
};

/** GET /api/v1/shinology/testimonial/list?page&limit&category */
export const getTestimonialList = (callback: Function = () => {}) => {
  return (dispatch: Function, getState: Function) => {
    const { page, limit, category }: ReducersModal["TestimonialReducer"] =
      getState().TestimonialReducer;
    const resolvedCategory = toServiceCategory(category);
    const query = `?page=${page}&limit=${limit}&category=${resolvedCategory}`;

    if (listRequestKey === query) {
      return;
    }
    listRequestKey = query;

    dispatch({
      type: UPDATE_TESTIMONIAL_REDUCER,
      payload: { loader: true },
    });

    GET_API_CALL(
      ENDPOINTS.TESTIMONIAL.LIST,
      query,
      (response: APIResponseModal) => {
        if (listRequestKey === query) listRequestKey = "";
        const current = getState().TestimonialReducer;
        const isStale =
          toServiceCategory(current.category) !== resolvedCategory ||
          Number(current.page) !== Number(page) ||
          Number(current.limit) !== Number(limit);
        if (isStale) {
          return;
        }

        if (response.isSuccess) {
          const mapped = mapTestimonialListResponse(response.data);
          attachDisplayUrls(mapped.result).then((result) => {
            const latest = getState().TestimonialReducer;
            if (
              toServiceCategory(latest.category) !== resolvedCategory ||
              Number(latest.page) !== Number(page) ||
              Number(latest.limit) !== Number(limit)
            ) {
              return;
            }
            dispatch({
              type: UPDATE_TESTIMONIAL_REDUCER,
              payload: {
                result,
                total: mapped.total,
                totalPages: mapped.totalPages,
                nextHit: mapped.nextHit,
                pagination: mapped.pagination,
                loader: false,
              },
            });
            callback(result);
          });
          return;
        } else {
          dispatch({
            type: UPDATE_TESTIMONIAL_REDUCER,
            payload: { loader: false, result: [] },
          });
          callback([]);
        }
      },
    );
  };
};

/** POST /api/v1/shinology/testimonial/add-testimonial */
export const createTestimonial = (values: any, callback: Function) => {
  return (dispatch: Function) => {
    dispatch({
      type: UPDATE_TESTIMONIAL_REDUCER,
      payload: { loader: true },
    });
    POST_API_CALL(
      ENDPOINTS.TESTIMONIAL.CREATE,
      values,
      (response: APIResponseModal) => {
        if (response.isSuccess) {
          SHOW_TOAST(response?.data?.message, "success");
          dispatch(getTestimonialList());
        } else {
          dispatch({
            type: UPDATE_TESTIMONIAL_REDUCER,
            payload: { loader: false },
          });
        }
        callback(response);
      },
    );
  };
};

/** PATCH /api/v1/shinology/testimonial/update-testimonial/{id} */
export const updateTestimonial = (
  testimonialId: string,
  values: any,
  callback: Function,
  replacedS3Urls: string[] = [],
) => {
  return (dispatch: Function) => {
    if (!testimonialId) {
      callback({ isSuccess: false, data: null });
      return;
    }

    dispatch({
      type: UPDATE_TESTIMONIAL_REDUCER,
      payload: { loader: true },
    });

    PATCH_API_CALL(
      `${ENDPOINTS.TESTIMONIAL.UPDATE}/${testimonialId}`,
      values,
      (response: APIResponseModal) => {
        if (response.isSuccess) {
          SHOW_TOAST(response?.data?.message, "success");
          if (replacedS3Urls.length) {
            DELETE_S3_FILES(replacedS3Urls);
          }
          dispatch(getTestimonialList());
        } else {
          dispatch({
            type: UPDATE_TESTIMONIAL_REDUCER,
            payload: { loader: false },
          });
        }
        callback(response);
      },
    );
  };
};

/** DELETE /api/v1/shinology/testimonial/delete-testimonial/{id} */
export const deleteTestimonial = (
  testimonialId: string,
  callback: Function = () => {},
  s3Urls: string[] = [],
) => {
  return (dispatch: Function) => {
    if (!testimonialId) {
      callback({ isSuccess: false });
      return;
    }

    dispatch({
      type: UPDATE_TESTIMONIAL_REDUCER,
      payload: { loader: true },
    });

    DELETE_API_CALL(
      `${ENDPOINTS.TESTIMONIAL.DELETE}/${testimonialId}`,
      (response: APIResponseModal) => {
        if (response.isSuccess) {
          SHOW_TOAST(response?.data?.message, "success");
          if (s3Urls.length) {
            DELETE_S3_FILES(s3Urls);
          }
          dispatch(getTestimonialList());
        } else {
          dispatch({
            type: UPDATE_TESTIMONIAL_REDUCER,
            payload: { loader: false },
          });
        }
        callback(response);
      },
    );
  };
};
